import type {
  IssuedEnrollmentTokenRecord,
  LicensingPublicKeyRecord,
  RegistrationRequestIdentityRecord,
  LicensingStationRecord,
  RegistrationRequestRecord,
  ResolveInstallationResponse,
  StationAuditRecord,
  StationLicenseStateRecord,
} from '@/api/licensing';

export const LICENSING_QR_PREFIX = 'DSLI1:';
export const REGISTRATION_REQUEST_QR_PREFIX = 'DSLR1:';

export type LicensingPayloadKind = 'installation' | 'registration_request';

export type InstallationPayloadValidation =
  | { ok: true; payload: string; kind: LicensingPayloadKind }
  | { ok: false; message: string };

export type ResolveResultState =
  | { kind: 'registered'; title: string; mayShowStationDetails: true }
  | { kind: 'no_access'; title: string; mayShowStationDetails: false }
  | { kind: 'unregistered'; title: string; mayShowStationDetails: false };

export type StationListState = 'loading' | 'denied' | 'empty' | 'ready';
export type StationLifecycleTone = 'allowed' | 'blocked' | 'warning' | 'neutral';
export type StationLicenseDisplayTone = 'allowed' | 'blocked' | 'pending' | 'neutral';
export type RegistrationRequestApprovalMode = 'global' | 'client_scoped';

export interface LicensingRequestErrorView {
  title: string;
  detail: string;
}

export interface StationLifecycleDisplayState {
  title: string;
  detail: string;
  badge: string;
  tone: StationLifecycleTone;
  requiresReenroll: boolean;
}

export interface StationLicenseDisplayState {
  title: string;
  detail: string;
  badge: string;
  tone: StationLicenseDisplayTone;
  pendingRefresh: boolean;
  overrideApplied: boolean;
}

export interface EnrollmentTransferInput {
  issued: IssuedEnrollmentTokenRecord;
  publicKey: LicensingPublicKeyRecord;
  platformUrl: string;
  stateKey: string;
  baseDir?: string;
}

export interface EnrollmentTransferPayload {
  type: 'dslp.enrollment-transfer';
  version: 1;
  appId: string;
  clientId: number;
  token: string;
  expiresAt: string;
  platformUrl: string;
  platformPublicKey: string;
  platformPublicKeyVersion: number;
  stateKey: string;
}

export interface RegistrationRequestApprovalViewState {
  mode: RegistrationRequestApprovalMode;
  showApprovalControls: boolean;
  showClientSelector: boolean;
  targetClientId: number | null;
  canApprove: boolean;
  confirmationKey: string | null;
  isConfirmed: boolean;
  fixedClientMessage: string;
}

export function validateInstallationPayload(value: string): InstallationPayloadValidation {
  const payload = value.trim();
  if (!payload) {
    return { ok: false, message: 'Вставьте QR payload' };
  }
  if (payload.startsWith(LICENSING_QR_PREFIX)) {
    return { ok: true, payload, kind: 'installation' };
  }
  if (payload.startsWith(REGISTRATION_REQUEST_QR_PREFIX)) {
    return { ok: true, payload, kind: 'registration_request' };
  }
  return { ok: false, message: 'Это не QR установки лицензирования' };
}

export function buildInstallationPayloadFromRegistrationIdentity(identity: RegistrationRequestIdentityRecord): string {
  return `${LICENSING_QR_PREFIX}${identity.appId}:${identity.installationId}`;
}

export function getLicensingRequestErrorView(
  error: unknown,
  kind: LicensingPayloadKind,
): LicensingRequestErrorView {
  const status = getHttpStatus(error);
  if (status === 401) {
    return {
      title: 'Требуется вход',
      detail: 'Сессия истекла или не прошла проверку. Выйдите и войдите снова.',
    };
  }
  if (status === 403) {
    return {
      title: 'Недостаточно прав',
      detail: 'У текущей учетной записи нет доступа к этой операции или к выбранному клиенту.',
    };
  }
  if (status === 404 && kind === 'registration_request') {
    return {
      title: 'QR регистрации не найден',
      detail: 'Запрос регистрации не найден, истек или был создан для другого licensing API. Создайте новый QR в приложении.',
    };
  }
  if (status === 404) {
    return {
      title: 'Установка не найдена',
      detail: 'Станция не найдена на текущем licensing API. Проверьте, что приложение подключено к этому же серверу.',
    };
  }
  if (status && status >= 500) {
    return {
      title: 'Сервис лицензирования недоступен',
      detail: 'Повторите проверку позже. Если ошибка повторяется, проверьте состояние licensing API.',
    };
  }

  const serverMessage = getHttpErrorMessage(error);
  return {
    title: 'QR не распознан',
    detail: serverMessage || (error instanceof Error ? error.message : 'Не удалось проверить QR'),
  };
}

export function getResolveResultState(result: ResolveInstallationResponse): ResolveResultState {
  if (result.status === 'registered' && result.station) {
    return {
      kind: 'registered',
      title: 'Зарегистрирована',
      mayShowStationDetails: true,
    };
  }
  if (result.status === 'no_access') {
    return {
      kind: 'no_access',
      title: 'Нет доступа к клиенту',
      mayShowStationDetails: false,
    };
  }
  return {
    kind: 'unregistered',
    title: 'Не зарегистрирована',
    mayShowStationDetails: false,
  };
}

export function getStationListState(input: {
  isLoading: boolean;
  isError: boolean;
  itemCount: number;
}): StationListState {
  if (input.isLoading) return 'loading';
  if (input.isError) return 'denied';
  if (input.itemCount === 0) return 'empty';
  return 'ready';
}

export function getRegistrationRequestApprovalViewState(input: {
  request: Pick<RegistrationRequestRecord, 'status'>;
  requestId: string;
  appId: string;
  fixedClientId: number | null;
  selectedClientId: string;
  confirmedContextKey: string | null;
}): RegistrationRequestApprovalViewState {
  const fixedClientId = normalizePositiveClientId(input.fixedClientId);
  const selectedClientId = normalizePositiveClientId(input.selectedClientId);
  const targetClientId = fixedClientId ?? selectedClientId;
  const showApprovalControls = input.request.status === 'PENDING';
  const confirmationKey = targetClientId === null
    ? null
    : buildRegistrationApprovalContextKey(input.requestId, input.appId, targetClientId);
  const isConfirmed = confirmationKey !== null && input.confirmedContextKey === confirmationKey;

  return {
    mode: fixedClientId ? 'client_scoped' : 'global',
    showApprovalControls,
    showClientSelector: showApprovalControls && !fixedClientId,
    targetClientId,
    canApprove: showApprovalControls && targetClientId !== null && isConfirmed,
    confirmationKey,
    isConfirmed,
    fixedClientMessage: fixedClientId ? `Привязка будет выполнена к клиенту ${fixedClientId}.` : '',
  };
}

export function buildRegistrationApprovalContextKey(
  requestId: string,
  appId: string,
  clientId: number,
  targetId?: string,
): string {
  return [requestId, appId, clientId, targetId].filter((value) => value !== undefined).join('\u0000');
}

export function getStationLifecycleDisplayState(
  station?: Pick<
    LicensingStationRecord,
    'status' | 'decommissionedAt' | 'adminBan' | 'adminAllow' | 'outdated' | 'minSupportedVersion'
  >,
): StationLifecycleDisplayState {
  if (!station) {
    return {
      title: 'Станция недоступна',
      detail: 'Нет данных станции',
      badge: 'нет данных',
      tone: 'neutral',
      requiresReenroll: false,
    };
  }

  if (station.decommissionedAt || station.status === 'DECOMMISSIONED') {
    return {
      title: 'Требуется re-enroll',
      detail: 'Станция выведена из эксплуатации и должна пройти новую регистрацию',
      badge: 'decommissioned',
      tone: 'blocked',
      requiresReenroll: true,
    };
  }

  if (station.adminBan?.active) {
    return {
      title: 'Заблокирована администратором',
      detail: station.adminBan.reason || 'Активен admin ban',
      badge: 'admin ban',
      tone: 'blocked',
      requiresReenroll: false,
    };
  }

  if (station.adminAllow?.active) {
    return {
      title: 'Разрешена администратором',
      detail: station.adminAllow.reason || 'Активен admin allow',
      badge: 'admin allow',
      tone: 'allowed',
      requiresReenroll: false,
    };
  }

  if (station.outdated) {
    return {
      title: 'Версия устарела',
      detail: station.minSupportedVersion
        ? `Минимальная версия: ${station.minSupportedVersion}`
        : 'Версия ниже минимальной политики приложения',
      badge: 'outdated',
      tone: 'warning',
      requiresReenroll: false,
    };
  }

  if (station.status === 'HEALTHY') {
    return {
      title: 'Станция активна',
      detail: 'Последний статус станции рабочий',
      badge: 'healthy',
      tone: 'allowed',
      requiresReenroll: false,
    };
  }

  if (station.status === 'SUSPENDED' || station.status === 'DISABLED') {
    return {
      title: 'Станция ограничена',
      detail: 'Проверьте license-state и последние события',
      badge: station.status.toLowerCase(),
      tone: 'blocked',
      requiresReenroll: false,
    };
  }

  return {
    title: 'Станция под наблюдением',
    detail: 'Проверьте license-state и последние события',
    badge: station.status.toLowerCase() || 'unknown',
    tone: 'neutral',
    requiresReenroll: false,
  };
}

export function getStationLicenseDisplayState(input: {
  state?: StationLicenseStateRecord;
  isLoading: boolean;
}): StationLicenseDisplayState {
  if (input.isLoading && !input.state) {
    return {
      title: 'Проверка состояния',
      detail: 'Загрузка license-state',
      badge: 'проверка',
      tone: 'neutral',
      pendingRefresh: false,
      overrideApplied: false,
    };
  }

  const state = input.state;
  if (!state) {
    return {
      title: 'Состояние недоступно',
      detail: 'Нет данных license-state',
      badge: 'нет данных',
      tone: 'neutral',
      pendingRefresh: false,
      overrideApplied: false,
    };
  }

  if (
    state.effectiveAllowed &&
    state.reason === 'emergency_override' &&
    state.requiresRefresh &&
    !state.certCarriesActiveOverride
  ) {
    return {
      title: 'Разрешено на сервере',
      detail: 'Установка применит разрешение после refresh',
      badge: 'нужен refresh',
      tone: 'pending',
      pendingRefresh: true,
      overrideApplied: false,
    };
  }

  if (
    state.effectiveAllowed &&
    state.reason === 'emergency_override' &&
    state.certCarriesActiveOverride
  ) {
    return {
      title: 'Работа разрешена',
      detail: 'Override применен в сертификате станции',
      badge: 'override активен',
      tone: 'allowed',
      pendingRefresh: false,
      overrideApplied: true,
    };
  }

  if (state.effectiveAllowed) {
    return {
      title: 'Работа разрешена',
      detail: licenseReasonLabel(state.reason),
      badge: entitlementStatusLabel(state.entitlementStatus || 'ok'),
      tone: 'allowed',
      pendingRefresh: false,
      overrideApplied: false,
    };
  }

  return {
    title: 'Работа ограничена',
    detail: licenseReasonLabel(state.reason),
    badge: entitlementStatusLabel(state.entitlementStatus || state.reason),
    tone: 'blocked',
    pendingRefresh: false,
    overrideApplied: false,
  };
}

export function licenseReasonLabel(reason: string): string {
  switch (reason) {
    case 'entitlement_ok':
      return 'Клиент допущен по ds-main';
    case 'emergency_override':
      return 'Активно временное разрешение';
    case 'suspended':
      return 'Клиент не допущен по ds-main';
    case 'cert_expired':
      return 'Сертификат истек';
    case 'no_cert':
      return 'Нет сертификата станции';
    case 'station_inactive':
      return 'Станция неактивна';
    case 'admin_ban':
      return 'Станция заблокирована администратором';
    case 'admin_allow':
      return 'Станция разрешена администратором';
    default:
      return reason || 'Причина не указана';
  }
}

export function entitlementStatusLabel(status: string): string {
  switch (status) {
    case 'ok':
      return 'оплата ok';
    case 'warning':
      return 'предупреждение';
    case 'suspended':
      return 'оплата stop';
    default:
      return status || 'нет статуса';
  }
}

export function stationAuditEventLabel(event: string): string {
  switch (event) {
    case 'station_enrolled':
      return 'Регистрация станции';
    case 'station_refreshed':
      return 'Refresh станции';
    case 'refresh_failed':
      return 'Ошибка refresh';
    case 'cert_issued':
      return 'Выдан сертификат';
    case 'cert_issued_with_stale_status':
      return 'Выдан сертификат со stale-статусом';
    case 'override_issued':
      return 'Выдан 24h override';
    case 'override_blocked':
      return 'Override отклонен';
    case 'override_used':
      return 'Override использован';
    case 'station_decommissioned':
      return 'Станция выведена';
    case 'station_admin_banned':
      return 'Admin ban';
    case 'station_admin_allowed':
      return 'Admin allow';
    case 'station_admin_allow_used':
      return 'Admin allow использован';
    case 'station_policy_updated':
      return 'Политика станции изменена';
    default:
      return event || 'Событие';
  }
}

export function stationAuditPayloadPreview(entry: Pick<StationAuditRecord, 'payload'>): string {
  if (!entry.payload || typeof entry.payload !== 'object') {
    return '';
  }
  const payload = entry.payload as Record<string, unknown>;
  const values = [
    typeof payload.reason === 'string' ? payload.reason : '',
    typeof payload.status === 'string' ? payload.status : '',
    typeof payload.validUntil === 'string' ? `до ${payload.validUntil}` : '',
    typeof payload.overrideId === 'string' ? `override ${payload.overrideId}` : '',
  ].filter(Boolean);
  return values.join(' · ');
}

export function generateStateKeyHex(randomBytes: Uint8Array = randomBytes32()): string {
  if (randomBytes.length !== 32) {
    throw new Error('state key must be 32 bytes');
  }
  return Array.from(randomBytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function buildEnrollmentTransferPayload(input: EnrollmentTransferInput): EnrollmentTransferPayload {
  return {
    type: 'dslp.enrollment-transfer',
    version: 1,
    appId: input.issued.appId,
    clientId: input.issued.clientId,
    token: input.issued.token,
    expiresAt: input.issued.expiresAt,
    platformUrl: normalizedURL(input.platformUrl),
    platformPublicKey: input.publicKey.publicKey,
    platformPublicKeyVersion: input.publicKey.version,
    stateKey: input.stateKey,
  };
}

export function buildEnrollmentTransferJSON(input: EnrollmentTransferInput): string {
  return JSON.stringify(buildEnrollmentTransferPayload(input), null, 2);
}

export function buildMailarchiverEnv(input: EnrollmentTransferInput): string {
  const payload = buildEnrollmentTransferPayload(input);
  return [
    `MAILARCHIVER_LICENSE_PLATFORM_URL=${shellQuote(payload.platformUrl)}`,
    `MAILARCHIVER_LICENSE_PLATFORM_PUBLIC_KEY=${shellQuote(payload.platformPublicKey)}`,
    `MAILARCHIVER_LICENSE_STATE_KEY=${shellQuote(payload.stateKey)}`,
    `MAILARCHIVER_LICENSE_TOKEN=${shellQuote(payload.token)}`,
  ].join('\n');
}

export function buildMailarchiverEnrollCommand(input: EnrollmentTransferInput): string {
  const baseDir = input.baseDir || '/var/lib/mailarchiver';
  return [
    buildMailarchiverEnv(input),
    `mailarchiver --base-dir ${shellQuote(baseDir)} --json license enroll`,
  ].join('\n');
}

function normalizedURL(value: string): string {
  return value.trim().replace(/\/+$/, '');
}

function normalizePositiveClientId(value: number | string | null | undefined): number | null {
  const parsed = typeof value === 'number' ? value : Number(value || 0);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function getHttpStatus(error: unknown): number | null {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return null;
  }
  const response = (error as { response?: { status?: unknown } }).response;
  return typeof response?.status === 'number' ? response.status : null;
}

function getHttpErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return '';
  }
  const data = (error as { response?: { data?: unknown } }).response?.data;
  if (!data || typeof data !== 'object') {
    return '';
  }
  const message = (data as { message?: unknown }).message;
  if (typeof message === 'string') {
    return message;
  }
  if (Array.isArray(message)) {
    return message.filter((item): item is string => typeof item === 'string').join(', ');
  }
  return '';
}

function shellQuote(value: string): string {
  return `'${value.replace(/'/g, `'\\''`)}'`;
}

function randomBytes32(): Uint8Array {
  const bytes = new Uint8Array(32);
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
}
